import axios from 'axios';

// Create an axios instance with default configuration for API gateway
const API_TIMEOUT = parseInt(process.env.REACT_APP_API_TIMEOUT) || 5000; // Reduce timeout for testing (5 seconds)
const apiClient = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000',
  timeout: API_TIMEOUT
});

// Create an axios instance with default configuration for video service (direct)
const VIDEO_SERVICE_TIMEOUT = parseInt(process.env.REACT_APP_VIDEO_SERVICE_TIMEOUT) || 300000; // 5 minutes default for video service (upload operations)
const videoServiceClient = axios.create({
  baseURL: process.env.REACT_APP_VIDEO_SERVICE_URL || 'http://localhost:3002',
  timeout: VIDEO_SERVICE_TIMEOUT
});

// Add a request interceptor to include the authorization token for API gateway
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add a request interceptor to include the authorization token for video service
videoServiceClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add a response interceptor to handle common error responses
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response && error.response.status === 401) {
      // Unauthorized - remove token and redirect to login
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    // Handle circular reference errors by creating a clean error object
    if (error.toJSON) {
      // Axios errors have a toJSON method, but it can fail with circular references
      try {
        // Try to serialize the error normally
        JSON.stringify(error);
      } catch (serializeError) {
        // If serialization fails, create a clean error object
        const cleanError = {
          message: error.message || 'Network Error',
          code: error.code || 'ERR_NETWORK',
          response: error.response ? {
            status: error.response.status,
            statusText: error.response.statusText,
            data: error.response.data
          } : undefined
        };
        return Promise.reject(cleanError);
      }
    }
    return Promise.reject(error);
  }
);

// Add a response interceptor for video service client to handle rate limiting
videoServiceClient.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response && error.response.status === 401) {
      // Unauthorized - remove token and redirect to login
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    // Handle circular reference errors by creating a clean error object
    if (error.toJSON) {
      // Axios errors have a toJSON method, but it can fail with circular references
      try {
        // Try to serialize the error normally
        JSON.stringify(error);
      } catch (serializeError) {
        // If serialization fails, create a clean error object
        const cleanError = {
          message: error.message || 'Network Error',
          code: error.code || 'ERR_NETWORK',
          response: error.response ? {
            status: error.response.status,
            statusText: error.response.statusText,
            data: error.response.data
          } : undefined
        };
        return Promise.reject(cleanError);
      }
    }
    return Promise.reject(error);
  }
);

// Auth API functions
export const authAPI = {
  register: (data) => apiClient.post('/api/register', data),
  login: (data) => apiClient.post('/api/login', data),
  getProfile: () => apiClient.get('/api/profile'),
  updateProfile: (data) => apiClient.put('/api/user/profile', data),
  changePassword: (data) => apiClient.put('/api/user/change-password', data),
  forgotPassword: (data) => apiClient.post('/api/forgot-password', data),
  verifyResetToken: (token) => apiClient.get(`/api/reset-password/${token}`),
  resetPassword: (token, data) => apiClient.post(`/api/reset-password/${token}`, data),
  verifyEmailToken: (token) => apiClient.get(`/api/verify-email/${token}`),
  resendVerification: (data) => apiClient.post('/api/resend-verification', data),
  getAccountStats: () => apiClient.get('/api/account-stats'),
  getSystemHealth: () => apiClient.get('/api/system-health'),
  getNews: () => apiClient.get('/api/news'),
  createNews: (data) => apiClient.post('/api/news', data),
  updateNews: (id, data) => apiClient.put(`/api/news/${id}`, data),
  deleteNews: (id) => apiClient.delete(`/api/news/${id}`),
  getCSRFToken: () => apiClient.get('/api/csrf-token'),
  createPaymentIntentForVideoProcessing: (paymentData) => apiClient.post('/api/billing/create-video-processing-payment-intent', paymentData),
};

// Coupon API functions
export const couponAPI = {
  applyCoupon: (code, userId, csrfToken) => apiClient.post(`/api/coupons/apply/${code}/${userId}`, {}, {
    headers: {
      'x-csrf-token': csrfToken
    }
  }),
  getUserCoupons: (userId) => apiClient.get(`/api/coupons/user/${userId}`),
  getUserCoupon: (userId, code) => apiClient.get(`/api/coupons/user/${userId}/${code}`),

  // Admin coupon management functions
  getAllCoupons: () => apiClient.get('/api/admin/coupons'),
  createCoupon: async (data, csrfToken) => {
    const headers = {};
    if (csrfToken) {
      headers['x-csrf-token'] = csrfToken;
    }
    return apiClient.post('/api/admin/coupons', data, { headers });
  },
  updateCoupon: async (id, data, csrfToken) => {
    const headers = {};
    if (csrfToken) {
      headers['x-csrf-token'] = csrfToken;
    }
    return apiClient.put(`/api/admin/coupons/${id}`, data, { headers });
  },
  deleteCoupon: async (id, csrfToken) => {
    const headers = {};
    if (csrfToken) {
      headers['x-csrf-token'] = csrfToken;
    }
    return apiClient.delete(`/api/admin/coupons/${id}`, { headers });
  }
};

// Utility function to get CSRF token
export const getCSRFToken = async () => {
  try {
    const response = await apiClient.get('/api/csrf-token');
    // Store the token in localStorage for later use
    if (response.headers && response.headers['x-csrf-token']) {
      localStorage.setItem('csrfToken', response.headers['x-csrf-token']);
      return response.headers['x-csrf-token'];
    } else if (response.data && response.data.csrfToken) {
      localStorage.setItem('csrfToken', response.data.csrfToken);
      return response.data.csrfToken;
    }
    return localStorage.getItem('csrfToken');
  } catch (error) {
    console.error('Failed to get CSRF token:', error);
    return localStorage.getItem('csrfToken'); // fallback to existing token
  }
};

// Video API functions
export const videoAPI = {
  // Use videoServiceClient for upload-related endpoints (direct to video service)
  upload: (formData, onUploadProgress) => videoServiceClient.post('/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    onUploadProgress: onUploadProgress,
    timeout: parseInt(process.env.REACT_APP_UPLOAD_TIMEOUT) || 30000 // Increased timeout to 30 seconds
  }),
  // Chunked upload functions (direct to video service)
  getCSRFToken: (userId) => videoServiceClient.post(`/csrf-token/${userId}`, {}, {
    timeout: parseInt(process.env.REACT_APP_CSRF_TOKEN_TIMEOUT) || 30000 // 30 seconds timeout
  }),
  initializeUploadSession: (userId, uploadData, csrfToken) => videoServiceClient.post(`/upload-session/${userId}`, uploadData, {
    headers: {
      'X-CSRF-Token': csrfToken
    },
    timeout: parseInt(process.env.REACT_APP_UPLOAD_SESSION_TIMEOUT) || 30000 // 30 seconds timeout
  }),
  uploadChunk: (userId, sessionId, chunkIndex, chunkData, onUploadProgress, csrfToken) => {
    // Track the progress of this specific chunk
    let chunkProgress = 0;

    return videoServiceClient.post(`/upload-chunk/${userId}/${sessionId}/${chunkIndex}`, chunkData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        'X-CSRF-Token': csrfToken
      },
      onUploadProgress: (progressEvent) => {
        // Convert axios progress event to simple percentage for this chunk
        if (progressEvent && progressEvent.total) {
          chunkProgress = Math.round((progressEvent.loaded / progressEvent.total) * 100);
          if (typeof onUploadProgress === 'function') {
            onUploadProgress(chunkProgress);
          }
        }
      },
      timeout: parseInt(process.env.REACT_APP_UPLOAD_CHUNK_TIMEOUT) || 300000 // Increased timeout to 5 minutes (300 seconds) for chunk uploads to match server timeout
    });
  },
  getUploadSessionStatus: (userId, sessionId) => videoServiceClient.get(`/upload-session/${userId}/${sessionId}`, {
    timeout: parseInt(process.env.REACT_APP_UPLOAD_SESSION_STATUS_TIMEOUT) || 120000 // 2 minutes timeout to allow for processing
  }),
  
  // Use apiClient for other endpoints (through API gateway)
  getVideos: () => apiClient.get('/api/videos'),
  getVideoResults: (videoId) => apiClient.get(`/api/videos/${videoId}/results`),
  
  // Download video results
  downloadVideoResults: async (videoId, format) => {
    try {
      const response = await apiClient.get(`/api/videos/${videoId}/download/${format}`, {
        responseType: 'blob'
      });
      return response;
    } catch (error) {
      // Handle circular reference errors
      if (error && typeof error === 'object') {
        try {
          // Try to serialize the error normally
          JSON.stringify(error);
        } catch (serializeError) {
          // If serialization fails, create a clean error object
          const cleanError = {
            message: error.message || 'Network Error',
            code: error.code || 'ERR_NETWORK',
            response: error.response ? {
              status: error.response.status,
              statusText: error.response.statusText,
              data: error.response.data
            } : undefined
          };
          throw cleanError;
        }
      }
      throw error;
    }
  },
  
  // Download group results
  downloadGroupResults: async (groupId, format) => {
    try {
      const response = await apiClient.get(`/api/videos/group/${groupId}/download/${format}`, {
        responseType: 'blob'
      });
      return response;
    } catch (error) {
      // Handle circular reference errors
      if (error && typeof error === 'object') {
        try {
          // Try to serialize the error normally
          JSON.stringify(error);
        } catch (serializeError) {
          // If serialization fails, create a clean error object
          const cleanError = {
            message: error.message || 'Network Error',
            code: error.code || 'ERR_NETWORK',
            response: error.response ? {
              status: error.response.status,
              statusText: error.response.statusText,
              data: error.response.data
            } : undefined
          };
          throw cleanError;
        }
      }
      throw error;
    }
  },

  // Delete video by ID
  deleteVideo: async (videoId) => {
    // Get CSRF token first
    const csrfResponse = await authAPI.getCSRFToken();
    const csrfToken = csrfResponse.data.csrfToken;

    // Make delete request with CSRF token
    return apiClient.delete(`/api/videos/${videoId}`, {
      headers: {
        'X-CSRF-Token': csrfToken
      }
    });
  },

  // Update video name
  updateVideoName: async (videoId, newVideoName) => {
    // Get CSRF token first
    const csrfResponse = await authAPI.getCSRFToken();
    const csrfToken = csrfResponse.data.csrfToken;

    // Make update request with CSRF token
    return apiClient.put(`/api/videos/${videoId}/name`, {
      originalName: newVideoName
    }, {
      headers: {
        'X-CSRF-Token': csrfToken
      }
    });
  },

  // Update video location
  updateVideoLocation: async (videoId, newLocation) => {
    // Get CSRF token first
    const csrfResponse = await authAPI.getCSRFToken();
    const csrfToken = csrfResponse.data.csrfToken;

    // Make update request with CSRF token
    return apiClient.put(`/api/videos/${videoId}/location`, {
      location: newLocation
    }, {
      headers: {
        'X-CSRF-Token': csrfToken
      }
    });
  },

  // Update video location for entire group
  updateVideoLocationForGroup: async (groupingId, newLocation) => {
    // Get CSRF token first
    const csrfResponse = await authAPI.getCSRFToken();
    const csrfToken = csrfResponse.data.csrfToken;

    // Make update request with CSRF token
    return apiClient.put(`/api/videos/group/${groupingId}/location`, {
      location: newLocation
    }, {
      headers: {
        'X-CSRF-Token': csrfToken
      }
    });
  }
};

// GDPR/CCPA Compliance API functions
export const complianceAPI = {
  exportUserData: (format = 'zip') => apiClient.post('/api/user-data-export', { format }),  // Changed default to 'zip'
  deleteUserData: (reason, action = 'request') => apiClient.post('/api/user-data-deletion', { reason, action }),
  getUserDeletionStatus: () => apiClient.get('/api/user-data-deletion'),
  cancelUserDeletion: () => apiClient.post('/api/user-data-deletion', { action: 'cancel' }),
  getConsentStatus: () => apiClient.get('/api/user-consent'),
  updateConsent: (consentGiven, purposes) => apiClient.post('/api/user-consent', { consentGiven, purposes }),
  getPrivacySettings: () => apiClient.get('/api/user-privacy'),
  updatePrivacySettings: (settings) => apiClient.put('/api/user-privacy', { settings })
};

export default apiClient;